import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button, Card, Checkbox, DefaultBlockContent, ExternalLink, Icon } from '~/components/shared';
import { BYOD_PRODUCT_ID } from '~/store/campaign/byod.constants';
import { SimpleSummary } from '../../campaign/components/simple-summary/SimpleSummary';
import SimpleTotal from '../../campaign/components/simple-total/SimpleTotal';
import { useBasket } from '../useBasket';
import { MappedCampaign } from '../v2.utils';

import { captureException, captureMessage } from '@sentry/react';
import { useState } from 'react';
import { trackPurchaseEvent } from '~/analytics/eventTracker';
import useModal from '~/components/modal/Modal';
import ErrorModal from '~/components/modal/modals/ErrorModal/ErrorModal';
import { CustomerType } from '~/models/CustomerType';
import { ProductType } from '~/models/product/ProductType';
import { toBasketProduct } from '~/store/basket/basket.helpers';
import { getCampaign, mapCampaignData } from '~/store/campaign/api';
import { splitStreetAddresses } from '~/utils/address';
import { getValidDate } from '~/utils/validation';
import { type CheckoutRequest, checkout as checkoutRequest } from '../../../../store/checkout/api';
import '../../campaign/summary/summary.scss';
import { defaultDwellingUnit, defaultPartnerAlias } from '../constants';
import { decodeJwt } from '~/utils/jwt';

export const SummaryV2: React.FC = () => {
  const campaign = useOutletContext<MappedCampaign>();

  const selectedProducts = useBasket((s) => s.selectedProducts);
  const contactInfo = useBasket((s) => s.contactInfo);

  const mainSubscription = selectedProducts.find((product) => product.productType === ProductType.Subscription);

  const { acceptedTerms, isCheckoutAttempted, isDelayedActivation, onCheckout, setAcceptedTerms } = useCheckout(campaign);

  if (!contactInfo || !mainSubscription) {
    // This is already checked in step verification, see StepNavigationV2
    // So here we only do it for typescript
    return null;
  }

  const hideShipping = selectedProducts.some((p) => p.id === BYOD_PRODUCT_ID);
  const paidInstallationProducts = selectedProducts.filter((p) => p.productType === ProductType.Installation && p.pricing.price !== 0);
  const termsError =
    isCheckoutAttempted && !acceptedTerms ? 'Vilkår og personvernerklæring må godtas for å kunne fullføre bestillingen' : undefined;

  return (
    <div>
      <div className="summary">
        <section className="section">
          <Card>
            <h2 className="title-3">
              <Icon type="cart" flip="horizontal" size={1.5} />
              Se over og fullfør bestillingen
            </h2>
            <SimpleSummary selectedProducts={selectedProducts} hideShipping={hideShipping} />
          </Card>
          <Card color="gray-dark">
            <SimpleTotal selectedProducts={selectedProducts} />
          </Card>
          {campaign.additionalTerms && (
            <div className="additional-terms">
              <DefaultBlockContent blocks={campaign.additionalTerms} />
            </div>
          )}
        </section>
        <section className="section instructions-and-conditions">
          {/* PAYMENT */}
          <h2 className="title-3">
            <Icon type="formCheck" size={1.5} />
            {isDelayedActivation ? 'Aktivering og betaling' : 'Betaling'}
          </h2>
          <p>
            {isDelayedActivation
              ? 'TV-pakken aktiverer du enkelt på Min side når du er klar til å se TV. Abonnementskostnaden begynner ikke å løpe før du har aktivert TV-pakken. Faktura for utstyret mottar du sammen med utstyret i posten. '
              : 'Første faktura kommer i posten. '}
            Du kan sette opp AvtaleGiro eller eFaktura på Min side. Da slipper du fakturagebyr og sparer penger og miljø.
          </p>
          {/* DELIVERY */}
          <h2 className="title-3">
            <Icon type="box" size={1.5} />
            Levering
          </h2>
          <p className="delivery-lead">Faktura og utstyr vil bli sendt til:</p>
          <Card>
            <div className="delivery-address button-or-bold" data-testid="summary-delivery-address">
              <p>
                {contactInfo.firstName} {contactInfo.lastName}
              </p>
              <p>{contactInfo.street}</p>
              <p>
                {contactInfo.zip} {contactInfo.city}
              </p>
            </div>
          </Card>
          <p>Du kan hente utstyret på Posten, eller velge gratis hjemlevering. Dette valget får du når du mottar en SMS fra Bring.</p>
          {/* INSTALLATION PRODUCTS INFO */}
          {paidInstallationProducts.length > 0 && (
            <>
              <h2 className="title-3">
                <Icon type="wrench" size={1.5} />
                Montering
              </h2>
              <p>Montøren vil ta kontakt med deg i løpet av noen dager for å avtale montering.</p>
            </>
          )}
          {/* TERMS */}
          <h2 className="title-3">
            <Icon type="form" size={1.5} />
            Forutsetninger og vilkår
          </h2>
          <p>
            Tilbudet forutsetter nytegning av et <b>{mainSubscription?.displayData.name}</b>-abonnement med 12 måneders binding. Det er 14
            dagers angrefrist og du kan si opp abonnementet på Min side etter 9 mnd. Oppsigelsestid er inneværende, pluss to måneder.
          </p>
          <Card>
            <Checkbox
              checked={acceptedTerms}
              label={TermsLabel}
              id="termsAndConditions"
              onChange={() => setAcceptedTerms(!acceptedTerms)}
              error={termsError}
              size={2}
              value="Godtar betingelser"
            />
          </Card>

          <Button type="primary" className="checkout-button" onClick={onCheckout}>
            Fullfør Bestilling
          </Button>
        </section>
      </div>
    </div>
  );
};

const TermsLabel = (
  <div aria-label="Jeg har lest og godtar RiksTVs vilkår og personvernerklæring">
    Jeg har lest og godtar {''}
    <ExternalLink className="link-style" href="https://www.rikstv.no/produkter/vilkar/abonnementsvilkar">
      RiksTVs vilkår{' '}
    </ExternalLink>{' '}
    og {''}
    <ExternalLink className="link-style" href="https://www.rikstv.no/personvern">
      personvernerklæring
    </ExternalLink>
  </div>
);

const useCheckout = (originalCampaign: MappedCampaign) => {
  const { open } = useModal();
  const navigate = useNavigate();
  const { selectedProducts, contactInfo, ...basketState } = useBasket();

  const [isCheckoutAttempted, setIsCheckoutAttempted] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const isDelayedActivation = basketState.useDelayedActivation;

  const onCheckout = async () => {
    if (!contactInfo) return;

    setIsCheckoutAttempted(true);
    if (!acceptedTerms || isCheckoutLoading) {
      return;
    }
    setIsCheckoutLoading(true);

    // resolve correct campaign now that we have potential other ISP partner
    const campaign =
      basketState.selectedISP?.partnerAlias === defaultPartnerAlias
        ? originalCampaign
        : await getCampaign(basketState.selectedISP?.partnerAlias || '', defaultDwellingUnit, originalCampaign.url)
            .then((data) => mapCampaignData(data))
            .catch((err) => {
              captureException(err, {
                fingerprint: ['bestill_checkout_error_get_campaign'],
                extra: { partnerAlias: basketState.selectedISP?.partnerAlias || '', campaignUrl: originalCampaign.url },
              });
            });

    if (!campaign) {
      open(<ErrorModal />);
      setIsCheckoutLoading(false);
      return;
    }

    // Ensure that BankId token is still valid, since it only lasts for 5 minutes (Vipps uses 15 minutes)
    if (basketState.selectedVerificationProvider === 'BankID') {
      const expiryEpochSeconds = decodeJwt<{ exp: number }>(basketState.verificationResponse?.authResp.access_token || '')?.exp;
      if (!expiryEpochSeconds || expiryEpochSeconds < Date.now() / 1000) {
        // TODO: display error modal, clear auth data and redirect back to verification step
        // open(<ErrorModal message="BankId verification data has expired. Please perform verification again" />);
        // setIsCheckoutLoading(false);
        // return;
        captureMessage('BankId token is expired at checkout', { level: 'warning' });
      }
    }

    const { street, houseNo, houseNoAlpha } = splitStreetAddresses(contactInfo.street);
    // biome-ignore lint/style/noNonNullAssertion: We verify that this value is set in StepNavigationV2
    const partner = basketState.selectedISP!;
    const isoBirthDateNoMs = (getValidDate(contactInfo.birthDate)?.toISOString() || new Date(0).toISOString()).replace('.000Z', 'Z');
    const checkoutRequestData: CheckoutRequest = {
      customerType: campaign.customerType as CustomerType,
      customerVerificationDetails: {
        provider: basketState.selectedVerificationProvider,
        token: basketState.verificationResponse?.authResp.access_token || '',
      },
      potentialCustomer: {
        email: contactInfo.email,
        firstName: contactInfo.firstName,
        lastName: contactInfo.lastName,
        mobilePhone: contactInfo.mobilePhone,
        birthDate: isoBirthDateNoMs,
      },
      partnerAlias: partner.partnerAlias,
      trackingCode: campaign.campaignCode || '',
      defaultAddress: {
        city: contactInfo.city,
        street: street,
        zip: contactInfo.zip,
        houseNo: houseNo,
        houseNoAlpha: houseNoAlpha,
      },
      isLaterActivation: isDelayedActivation,
      laterActivationDate: basketState.delayedActivationDate ? new Date(basketState.delayedActivationDate).toISOString() : undefined,
      offers: selectedProducts.map((product) => product.offer.compositeId).filter(Boolean),
    };
    await checkoutRequest(checkoutRequestData).then(
      (recieptId: string) => {
        const basketProducts = selectedProducts.map((p) => toBasketProduct(p, campaign.customerType as CustomerType));
        trackPurchaseEvent({
          currency: 'NOK',
          partnerName: partner.partnerName,
          transaction_id: recieptId,
          value: basketProducts.reduce((total, product) => total + product.price, 0),
          verification_method: basketState.selectedVerificationProvider,
          tax: 0,
          shipping: 0,
          coupon: campaign.campaignCode,
          products: basketProducts,
          funnelVersion: 2,
          delayedActivation: isDelayedActivation,
        });
        const searchParams = new URLSearchParams();
        searchParams.set('subscription', selectedProducts.find((p) => p.productType === ProductType.Subscription)?.displayData.name || '');
        selectedProducts
          .filter((p) => p.productType === ProductType.Premium)
          .map((p) => p.displayData.name)
          .forEach((name) => {
            searchParams.append('premiumProducts', name);
          });
        navigate(`/bekreftelse?${searchParams.toString()}`);
      },
      (err) => {
        // Open error modal
        open(<ErrorModal />);
        setIsCheckoutLoading(false);
        captureException(new Error('Post Checkout Failed', { cause: err }), {
          fingerprint: ['bestill_checkout_error'],
          extra: { checkoutRequestData },
        });
      },
    );
  };

  return { onCheckout, isCheckoutAttempted, acceptedTerms, setAcceptedTerms, isDelayedActivation };
};
